import React, { useEffect } from "react";
import { createPortal } from "react-dom";

import {
  AI_DASHBOARDS_HELP_VIDEO_URL,
  AI_DASHBOARDS_VIDEO_MODAL_HEIGHT,
  AI_DASHBOARDS_VIDEO_MODAL_WIDTH,
} from "./dashboardHelpVideo";

export interface DashboardHelpVideoModalProps {
  open: boolean;
  onClose: () => void;
  /** Host document for the portal (e.g. iframe `document` when the viewer runs in an iframe). */
  portalDocument?: Document;
  /** Optional override for the embedded YouTube URL. */
  videoUrl?: string;
}

export const DashboardHelpVideoModal: React.FC<DashboardHelpVideoModalProps> = ({
  open,
  onClose,
  portalDocument = document,
  videoUrl,
}) => {
  useEffect(() => {
    if (!open) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    portalDocument.defaultView?.addEventListener("keydown", handleKeyDown);

    return () => portalDocument.defaultView?.removeEventListener("keydown", handleKeyDown);
  }, [onClose, open, portalDocument]);

  if (!open) {
    return null;
  }

  const body = portalDocument.body;
  if (!body) {
    return null;
  }

  const iframeUrl = videoUrl ?? AI_DASHBOARDS_HELP_VIDEO_URL;

  const modal = (
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center bg-black/60 px-6 py-8 backdrop-blur-sm"
      onClick={onClose}
      role="presentation"
    >
      <section
        className="relative w-full max-w-[1040px] overflow-hidden rounded-2xl bg-black shadow-[0_30px_90px_rgba(15,23,42,0.38)]"
        onClick={event => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="How to use AI Dashboards"
      >
        <button
          type="button"
          className="absolute right-3 top-3 z-10 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-black/50 text-white transition-colors hover:bg-black/70"
          aria-label="Close video"
          onClick={onClose}
        >
          <span className="material-icons !text-[20px]">close</span>
        </button>

        <div
          className="relative w-full overflow-hidden"
          style={{
            aspectRatio: `${AI_DASHBOARDS_VIDEO_MODAL_WIDTH} / ${AI_DASHBOARDS_VIDEO_MODAL_HEIGHT}`,
          }}
        >
          <iframe
            src={iframeUrl}
            title="How to use AI Dashboards"
            width={AI_DASHBOARDS_VIDEO_MODAL_WIDTH}
            height={AI_DASHBOARDS_VIDEO_MODAL_HEIGHT}
            className="absolute inset-0 block bg-black"
            style={{
              width: "100%",
              height: "100%",
              border: "none",
            }}
            allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      </section>
    </div>
  );

  return createPortal(modal, body);
};
